import React, { useEffect, useRef, useState } from "react"
import Highcharts from "highcharts/highstock"
import HighchartsReact from "highcharts-react-official"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { DATE_PICKER_FORMAT, GRAPH_DATE_OPTIONS } from "../../utils"
import { CircularProgress, makeStyles } from "@material-ui/core"
import { useTheme } from "@material-ui/core/styles"
import moment from "moment"
import useUser from "../../utils/customHooks/useUser"

const useStyles = makeStyles((theme) => ({
	root: {
		minHeight: 630,
		width: "100%",
		"& div div:nth-child(2) svg:nth-child(1) > rect": {
			fill: "transparent",
		},
	},
	noData: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		minHeight: 70,
	},
	wrapper: {
		display: "block",
	},
	title: {
		display: "flex",
	},
	color: {},
	text: {},
	heading: {
		display: "flex",
	},
	subHeading: {
		display: "flex",
	},
	loaderWrapper: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		minHeight: 75,
	},
}))

const TimelineChart = ({ refetchList, navigator = true, showGraph = true, height = 650 }) => {
	const classes = useStyles()
	const theme = useTheme()
	const [t] = useTranslation("global")

	const maxY = useRef(null)

	const graphData = useSelector((state) => state.timeline.graph)
	const isLoading = useSelector((state) => state.timeline.graphLoading)
	const eventsList = useSelector((state) => state.events.selectedEvents)

	const { getUserNameById } = useUser()

	const options = {
		chart: {
			height: showGraph ? height : 80, //650 or 80 // Set the height of the chart
			//width: 700, // Set the width of the chart
			zooming: {
				mouseWheel: false,
			},
		},
		navigator: {
			enabled: navigator,
			handles: {
				enabled: showGraph ? true : false,
			},
			// events: {
			// 	click: function (event) {
			// 		console.log("Navigator clicked at x: " + event.xAxis[0].value)
			// 	},
			// },
		},
		rangeSelector: showGraph
			? {
					inputEnabled: false,
					rangeSelector: {
						buttons: [
							{
								type: "all",
								text: "All",
							},
						],
						selected: 0, // Set the initial selection to "All"
					},
			  }
			: { enabled: false },
		yAxis: {
			title: {
				text: showGraph ? t("chart.yAxis") : "",
			},
			opposite: false, // Display y-axis on the left
			min: 0, // Set minimum value to 0
			gridLineColor: "#D8D8D8", // Color of the grid lines
			gridLineWidth: 0.2, // Width of the grid lines
			labels: {
				enabled: showGraph ? true : false,
			},
		},
		xAxis: {
			title: {
				text: showGraph ? t("chart.xAxis") : "",
			},
			type: "datetime",
			labels: {
				enabled: showGraph ? true : false,
			},
		},
		title: {
			text: showGraph ? t("chart.title") : "",
		},
		series: [
			{
				type: "spline",
				//lineColor: "#243661",
				name: t("chart.yAxis"),
				data: [],
				tooltip: {
					valueDecimals: 0,
				},
				marker: {
					enabled: true,
					symbol: "circle",
					radius: 3,
					fillColor: theme.palette.dataColors["600"],
				},
			},
			{
				type: "flags",
				accessibility: {
					exposeAsGroupOnly: true,
					description: "Flagged events.",
				},
				data: [],
			},
		],
	}

	const updateEvents = () => {
		const chart = Highcharts.charts[Highcharts.charts.length - 1] // Get the latest chart
		const currentxAxis = chart?.options?.xAxis[0]
		if (chart) {
			chart.update({
				xAxis: {
					...currentxAxis,
					plotBands: eventsList?.map((event) => ({
						from: event.date,
						to: event.endDate,
						color: event?.colour,
					})),
				},
				chart: {
					events: {
						render: function () {
							var chart = this
							chart.xAxis[0].plotLinesAndBands.forEach(function (plotBand) {
								plotBand.svgElem.on("mouseover", function (event) {})
								plotBand.svgElem.on("mouseout", function () {})
							})

							//get the max y value
							const chartY = Highcharts.charts[Highcharts.charts.length - 1]
							const max = chartY.yAxis[0].getExtremes().dataMax
							if (max) {
								maxY.current = max
							}
						},
					},
				},
			})
		}
	}

	useEffect(() => {
		updateEvents()
	}, [eventsList])

	useEffect(() => {
		const chart = Highcharts.charts[Highcharts.charts.length - 1] // Get the latest chart
		if (chart) {
			chart.update({
				...options,
				rangeSelector: showGraph
					? {
							buttons: [
								{
									type: "all",
									text: "All",
								},
							],
							selected: 0, // Set the initial selection to "All"
					  }
					: { enabled: false },
				series: [
					{
						...options.series[0],
						data: graphData.length ? graphData : [],
						marker: {
							enabled: true,
							symbol: "circle",
							radius: 3,
							fillColor: theme.palette.dataColors["600"],
						},
					},
					{
						...options.series[1],
						type: "flags",
						accessibility: {
							exposeAsGroupOnly: true,
							description: "Flagged events.",
						},
						data: eventsList?.map((event, index) => {
							const eventY = Math.min(Math.max(1, (maxY.current / eventsList.length) * (index + 0.5)), maxY.current - 0.5)
							return {
								x: event.date, //getUtcDate(event?.start_date), //event?.start_date,
								title: event?.name,
								text: `<b>${t("event.field.startDate")}</b>: ${moment(event.start_date).format(
									DATE_PICKER_FORMAT
								)}<br/><b>${t("event.field.endDate")}</b>: ${moment(event.end_date).format(DATE_PICKER_FORMAT)}`,
								marker: {
									symbol: "M0,0 L10,0 L10,10 L0,10 Z M2,2 L8,2 L8,8 L2,8 Z", // Custom 10-character string symbol
								},
								style: {},
								y: eventY,
								color: event?.colour,
								createdBy: `<b>${t("event.field.createdBy")}</b>: ${getUserNameById(event?.userid)}`,
							}
						}),
						tooltip: {
							pointFormat:
								'<span style="font-size: 20px;color:{point.color}">\u25A0</span> <b> {point.title}</b><br/>{point.text}<br/>{point.createdBy}',
						},
					},
				],
				xAxis: {
					title: {
						text: showGraph ? t("chart.xAxis") : "",
					},
					type: "datetime",
					labels: {
						enabled: showGraph ? true : false,
					},
					events: {
						// setExtremes: function (event) {
						// 	const start = event.min ? new Date(event.min) : null
						// 	const end = event.max ? new Date(event.max) : null
						// 	// Handle timeline change event
						// 	console.log("Start Date: " + start + ", End Date: " + end)
						// 	if (start && end) {
						// 		refetchList({
						// 			sliderPicker: { start, end },
						// 			graphPicker: { start: "", end: "" },
						// 			selected: GRAPH_DATE_OPTIONS.SLIDER,
						// 		})
						// 	}
						// },
						afterSetExtremes: function (e) {
							if (e.trigger === "navigator" || e.trigger === "rangeSelectorButton") {
								var start = Highcharts.dateFormat("%Y-%m-%d", e.min)
								var end = Highcharts.dateFormat("%Y-%m-%d", e.max)
								//console.log("Start Date: " + startDate + ", End Date: " + endDate)
								if (start && end) {
									refetchList({
										sliderPicker: { start, end },
										graphPicker: { start: "", end: "" },
										selected: GRAPH_DATE_OPTIONS.SLIDER,
									})
								}
							}

							// Call your custom function here with start and end dates
							// customFunction(startDate, endDate);
						},
					},
				},
				plotOptions: {
					series: {
						point: {
							events: {
								click: function (event) {
									const clickedDate = new Date(event.point.x)
									if (clickedDate) {
										refetchList({
											graphPicker: { start: clickedDate, end: clickedDate },
											selected: GRAPH_DATE_OPTIONS.GRAPH_PICKER,
										})
									}
								},
							},
						},
					},
				},
			})

			updateEvents() //load the event once received the graph data
		}
	}, [graphData, eventsList, showGraph])

	return (
		<div style={{ width: "100%", position: "relative" }}>
			{isLoading ? (
				<div className={classes.loaderWrapper}>
					<CircularProgress size={20} />
				</div>
			) : graphData?.length > 0 ? (
				<div id="export-chart">
					<HighchartsReact
						highcharts={Highcharts}
						options={options}
						constructorType={"stockChart"}
						containerProps={{ style: { width: "100%" } }}
					/>
				</div>
			) : (
				<div className={classes.noData}>{t("graph.noRecordsFound")}</div>
			)}
		</div>
	)
}

export default React.memo(TimelineChart)
